import { useEffect, useMemo, useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { Loader2, Paperclip, Reply, Send, Smile, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from '@/components/feedback/toast';
import type { ViewMember } from '../api/communityView';
import { searchGifs, gifConfigured, type Gif } from '../api/giphy';
import type { Channel } from './channels';
import { avatarBg, avatarInitial, displayName } from './cvHelpers';

const TOOL_BTN =
  'grid h-9 w-9 flex-none place-items-center rounded-[10px] border-0 bg-transparent text-white/[0.72] transition-all duration-150 hover:bg-white/[0.06] hover:text-[#fafafa]';

// Emoji palette for the inline picker (inserted into the draft, not a reaction).
const EMOJI_PICKER = [
  '😀', '😂', '🤣', '😊', '😍', '😎', '🤩', '🥳',
  '😢', '😭', '😡', '🤔', '😴', '🙄', '😬', '🤝',
  '👍', '👎', '👏', '🙏', '💪', '🔥', '✨', '🎉',
  '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '💯',
  '👀', '🚀', '⭐', '✅', '❌', '⚡', '💎', '🏆',
];

// Quirky placeholder lines — picked at random per channel, in the spirit of
// Discord's comedic empty-input copy. Short, harmless, slightly unhinged.
const PLACEHOLDERS: ((c: string) => string)[] = [
  (c) => `Say something legendary in #${c}…`,
  () => `Type a message. Or a manifesto. Your call.`,
  (c) => `Drop a hot take in #${c} (we won't tell)`,
  () => `The cursor's blinking. It wants attention.`,
  () => `Words go here. Good ones, ideally.`,
  (c) => `Break the silence in #${c}…`,
  () => `New message, who dis?`,
  () => `Be the main character. Start typing.`,
];

interface Props {
  channel: Channel;
  members: ViewMember[];
  sending: boolean;
  replyTo: { name: string; excerpt: string } | null;
  onCancelReply: () => void;
  onUploadImage: (file: File) => Promise<string>;
  onSend: (body: string, media?: string | null) => void;
  onTyping: () => void;
}

interface MentionState {
  hits: ViewMember[];
  active: number;
  start: number;
}

interface PendingMedia {
  /** What gets stored/sent: a storage path (image) or external URL (gif). */
  value: string;
  /** Local preview URL for the chip. */
  preview: string;
  kind: 'image' | 'gif';
}

function handleOf(m: ViewMember): string {
  return m.profile?.handle || displayName(m.profile).replace(/\s+/g, '');
}

export function Composer({
  channel,
  members,
  sending,
  replyTo,
  onCancelReply,
  onUploadImage,
  onSend,
  onTyping,
}: Props) {
  const [draft, setDraft] = useState('');
  const [mention, setMention] = useState<MentionState | null>(null);
  const [pending, setPending] = useState<PendingMedia | null>(null);
  const [uploading, setUploading] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [gifOpen, setGifOpen] = useState(false);
  const [gifQuery, setGifQuery] = useState('');
  const [gifs, setGifs] = useState<Gif[]>([]);
  const [gifLoading, setGifLoading] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const placeholder = useMemo(
    () => PLACEHOLDERS[Math.floor(Math.random() * PLACEHOLDERS.length)]!(channel.name),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reshuffle per channel
    [channel.id, channel.name],
  );

  // Load GIFs when the picker opens or the query changes (debounced).
  useEffect(() => {
    if (!gifOpen) return;
    let cancelled = false;
    setGifLoading(true);
    const t = window.setTimeout(() => {
      searchGifs(gifQuery)
        .then((res) => {
          if (!cancelled) setGifs(res);
        })
        .catch(() => {
          if (!cancelled) toast('Could not load GIFs', 'error');
        })
        .finally(() => {
          if (!cancelled) setGifLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [gifOpen, gifQuery]);

  // Revoke object URLs for image previews when they're replaced/cleared.
  useEffect(() => {
    return () => {
      if (pending?.kind === 'image') URL.revokeObjectURL(pending.preview);
    };
  }, [pending]);

  function autoGrow() {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`;
  }

  function detectMention(value: string) {
    const ta = taRef.current;
    const pos = ta?.selectionStart ?? value.length;
    const before = value.slice(0, pos);
    const m = before.match(/(^|\s)@([\w_-]{0,20})$/);
    if (!m) {
      setMention(null);
      return;
    }
    const frag = m[2] ?? '';
    const query = frag.toLowerCase();
    const hits = members
      .filter((mem) => {
        const h = (mem.profile?.handle ?? '').toLowerCase();
        const n = displayName(mem.profile).toLowerCase();
        return !query || h.includes(query) || n.includes(query);
      })
      .slice(0, 6);
    setMention(hits.length ? { hits, active: 0, start: pos - frag.length - 1 } : null);
  }

  function insertMention(mem: ViewMember) {
    const ta = taRef.current;
    const pos = ta?.selectionStart ?? draft.length;
    const start = mention?.start ?? pos;
    const tag = `@${handleOf(mem)} `;
    const next = draft.slice(0, start) + tag + draft.slice(pos);
    setDraft(next);
    setMention(null);
    requestAnimationFrame(() => {
      const caret = start + tag.length;
      ta?.focus();
      ta?.setSelectionRange(caret, caret);
    });
  }

  function insertEmoji(emoji: string) {
    const ta = taRef.current;
    const pos = ta?.selectionStart ?? draft.length;
    const next = draft.slice(0, pos) + emoji + draft.slice(pos);
    setDraft(next);
    setEmojiOpen(false);
    requestAnimationFrame(() => {
      const caret = pos + emoji.length;
      ta?.focus();
      ta?.setSelectionRange(caret, caret);
      autoGrow();
    });
  }

  async function onPickImage(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast('Please choose an image file', 'error');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast('Image must be under 8 MB', 'error');
      return;
    }
    setUploading(true);
    try {
      const path = await onUploadImage(file);
      setPending({ value: path, preview: URL.createObjectURL(file), kind: 'image' });
    } catch (err) {
      toast((err as Error).message || 'Upload failed', 'error');
    } finally {
      setUploading(false);
    }
  }

  function pickGif(gif: Gif) {
    setPending({ value: gif.url, preview: gif.preview, kind: 'gif' });
    setGifOpen(false);
  }

  function openGif() {
    if (!gifConfigured()) {
      toast('GIFs are not configured yet — add VITE_GIPHY_KEY to enable them.', 'error');
      return;
    }
    setEmojiOpen(false);
    setGifOpen((v) => !v);
  }

  function submit() {
    const body = draft.trim();
    if ((!body && !pending) || sending || uploading) return;
    onSend(body, pending?.value ?? null);
    setDraft('');
    setPending(null);
    setMention(null);
    requestAnimationFrame(() => {
      if (taRef.current) taRef.current.style.height = 'auto';
    });
  }

  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (mention) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMention({ ...mention, active: (mention.active + 1) % mention.hits.length });
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMention({ ...mention, active: (mention.active - 1 + mention.hits.length) % mention.hits.length });
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        const pick = mention.hits[mention.active];
        if (pick) {
          e.preventDefault();
          insertMention(pick);
          return;
        }
      }
      if (e.key === 'Escape') {
        setMention(null);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }

  return (
    <div className="relative mx-[22px] mb-[18px] mt-1 flex-none rounded-md border border-white/10 bg-white/[0.03] backdrop-blur-[8px] focus-within:border-white/[0.18] focus-within:bg-white/[0.05] focus-within:shadow-[0_6px_24px_rgba(0,0,0,0.25)]">
      {mention && (
        <div className="absolute inset-x-2 bottom-full z-[5] mb-1.5 rounded-xl border border-white/10 bg-[rgba(16,16,22,0.98)] p-1 shadow-[0_12px_36px_rgba(0,0,0,0.6)]">
          {mention.hits.map((mem, i) => (
            <div
              key={mem.user_id}
              className={cn(
                'flex cursor-pointer items-center gap-2.5 rounded-sm px-2.5 py-1.5 hover:bg-white/[0.08]',
                i === mention.active && 'bg-white/[0.08]',
              )}
              onMouseDown={(e) => {
                e.preventDefault();
                insertMention(mem);
              }}
            >
              <div className="grid h-6 w-6 flex-none place-items-center rounded-full bg-white/[0.12] bg-cover bg-center text-[11px] font-extrabold text-white" style={avatarBg(mem.profile?.avatar_url)}>
                {!mem.profile?.avatar_url && avatarInitial(displayName(mem.profile))}
              </div>
              <span className="text-[13px] font-bold text-[#fafafa]">{displayName(mem.profile)}</span>
              <span className="ml-auto text-xs text-white/[0.46]">@{handleOf(mem)}</span>
            </div>
          ))}
        </div>
      )}

      {emojiOpen && (
        <>
          <div className="fixed inset-0 z-[55]" onClick={() => setEmojiOpen(false)} />
          <div className="absolute bottom-full right-2 z-[60] mb-1.5 grid w-[296px] grid-cols-8 gap-0.5 rounded-xl border border-white/10 bg-[rgba(16,16,22,0.98)] p-2 shadow-[0_12px_36px_rgba(0,0,0,0.6)]">
            {EMOJI_PICKER.map((e) => (
              <button
                key={e}
                className="grid h-8 w-8 place-items-center rounded-[7px] text-lg leading-none transition-transform duration-100 hover:scale-[1.35]"
                onClick={() => insertEmoji(e)}
              >
                {e}
              </button>
            ))}
          </div>
        </>
      )}

      {gifOpen && (
        <>
          <div className="fixed inset-0 z-[5]" onClick={() => setGifOpen(false)} />
          <div className="absolute bottom-full left-2 z-[6] mb-1.5 w-[340px] rounded-xl border border-white/10 bg-[rgba(16,16,22,0.98)] p-2 shadow-[0_12px_36px_rgba(0,0,0,0.6)]">
            <input
              autoFocus
              className="mb-2 w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-[13px] text-[#fafafa] outline-none placeholder:text-white/[0.46] focus:border-white/20"
              placeholder="Search Tenor…"
              value={gifQuery}
              onChange={(e) => setGifQuery(e.target.value)}
            />
            <div className="no-scrollbar grid max-h-[260px] grid-cols-2 gap-1.5 overflow-y-auto">
              {gifLoading && (
                <div className="col-span-2 grid place-items-center py-8 text-white/[0.46]">
                  <Loader2 size={20} className="animate-spin" />
                </div>
              )}
              {!gifLoading && gifs.length === 0 && (
                <div className="col-span-2 py-8 text-center text-[13px] text-white/[0.46]">No GIFs found.</div>
              )}
              {!gifLoading &&
                gifs.map((g) => (
                  <button
                    key={g.id}
                    className="overflow-hidden rounded-lg border border-white/10 transition-transform hover:scale-[1.02]"
                    onClick={() => pickGif(g)}
                    title={g.description}
                  >
                    <img src={g.preview} alt={g.description} className="h-[90px] w-full object-cover" loading="lazy" />
                  </button>
                ))}
            </div>
            <div className="mt-1.5 text-right text-[9px] uppercase tracking-[0.5px] text-white/[0.3]">Powered by GIPHY</div>
          </div>
        </>
      )}

      {replyTo && (
        <div className="flex items-center gap-2 border-b border-white/[0.06] px-3 py-1.5 text-[12px] text-white/[0.46]">
          <Reply size={13} className="-scale-x-100" />
          <span>
            Replying to <span className="font-semibold text-white/[0.72]">{replyTo.name}</span>
          </span>
          {replyTo.excerpt && <span className="min-w-0 flex-1 truncate text-white/[0.4]">{replyTo.excerpt}</span>}
          <button className="ml-auto grid h-5 w-5 place-items-center rounded-full hover:bg-white/[0.08]" onClick={onCancelReply} title="Cancel reply">
            <X size={13} />
          </button>
        </div>
      )}

      {pending && (
        <div className="flex items-center gap-2 px-3 pt-2.5">
          <div className="relative">
            <img src={pending.preview} alt="" className="h-16 w-16 rounded-lg border border-white/10 object-cover" />
            <button
              className="absolute -right-1.5 -top-1.5 grid h-5 w-5 place-items-center rounded-full border border-white/15 bg-black/80 text-white hover:bg-black"
              onClick={() => setPending(null)}
              title="Remove"
            >
              <X size={11} />
            </button>
          </div>
          <span className="text-[11px] uppercase tracking-[0.5px] text-white/[0.46]">{pending.kind}</span>
        </div>
      )}

      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickImage} />

      <div className="flex items-end gap-1.5 py-2 pl-2.5 pr-2">
        <button className={TOOL_BTN} title="Attach image" disabled={uploading} onClick={() => fileRef.current?.click()}>
          {uploading ? <Loader2 size={18} className="animate-spin" /> : <Paperclip size={18} />}
        </button>
        <button
          className={cn(
            'grid h-9 flex-none place-items-center rounded-[10px] border-0 bg-transparent px-2 text-[12px] font-extrabold tracking-[0.5px] text-white/[0.72] transition-colors duration-150 hover:text-[#fafafa]',
            gifOpen && 'text-[#fafafa]',
          )}
          title="GIF"
          onClick={openGif}
        >
          GIF
        </button>
        <textarea
          ref={taRef}
          className="max-h-[200px] min-h-[22px] flex-1 resize-none border-0 bg-transparent px-1 py-2 text-[14.5px] leading-[1.5] text-[#fafafa] outline-none placeholder:text-white/[0.46]"
          rows={1}
          placeholder={placeholder}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            autoGrow();
            detectMention(e.target.value);
            onTyping();
          }}
          onKeyDown={onKey}
        />
        <button
          className={cn(
            'grid h-9 w-9 flex-none place-items-center rounded-[10px] border-0 bg-transparent text-white/[0.72] transition-colors duration-150 hover:text-[#fafafa]',
            emojiOpen && 'text-[#fafafa]',
          )}
          title="Emoji"
          onClick={() => {
            setGifOpen(false);
            setEmojiOpen((v) => !v);
          }}
        >
          <Smile size={18} />
        </button>
        <button
          className="grid h-9 w-9 flex-none place-items-center rounded-[10px] border-0 bg-[#fafafa] text-[#0a0a0e] shadow-[0_4px_18px_rgba(0,0,0,0.4)] transition-all duration-150 disabled:cursor-not-allowed disabled:bg-white/[0.08] disabled:text-white/[0.46] disabled:shadow-none"
          title="Send"
          disabled={(!draft.trim() && !pending) || sending || uploading}
          onClick={submit}
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  );
}
